import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FeatherModule } from 'angular-feather';

@Component({
  selector: 'app-custom-select',
  standalone: true,
  imports: [CommonModule, TranslateModule, FeatherModule],
  templateUrl: './custom-select.component.html',
  styleUrls: ['./custom-select.component.scss'],
})
export class CustomSelectComponent implements OnChanges, OnDestroy {
  @Input() options: any[] = [];
  @Input() selectedValue: any = '';
  @Input() placeholder: string = '';
  @Input() showAllOption: boolean = false;
  @Input() labelsAlreadyTranslated: boolean = false;
  /** When true, `selectedValue` is a `T[]`; options toggle on click; panel stays open until click outside. */
  @Input() multiple = false;
  /**
   * When more than this many values are selected, the trigger shows a short summary
   * (`CustomSelect.multipleItemsSelected`) instead of listing every label.
   */
  @Input() multipleSummaryAfter = 300;
  @Input() disabled: boolean = false;
  /**
   * When true (single-select only), the closed trigger always shows {@link placeholder} text instead
   * of the selected option label. Selection is unchanged for the panel and `selectedValue` binding.
   */
  @Input() alwaysShowPlaceholder = false;
  /**
   * `overlay` (default): dropdown is fixed to the viewport (escapes overflow/stacking parents).
   * `inline`: dropdown is in normal flow so parent containers (e.g. modals) can grow with the list.
   */
  @Input() panelLayout: 'overlay' | 'inline' = 'overlay';
  @Output() selectedValueChange = new EventEmitter<any>();

  @ViewChild('wrapper') wrapper!: ElementRef<HTMLElement>;
  @ViewChild('triggerBtn') triggerBtn!: ElementRef<HTMLButtonElement>;

  isOpen = false;
  hoveredValue: any = null;
  /** Ignore the document click that immediately follows opening. */
  private suppressOutsideCloseUntil = 0;
  /** Internal display value: used so the correct option stays active when parent doesn't bind [selectedValue] back. */
  private _displayValue: any = undefined;
  /** Fixed panel coordinates for overlay mode (avoids clipped/unclickable menus in toolbars). */
  panelPosition: { top: string; left: string; minWidth: string } | null = null;

  constructor(
    public translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) {}

  /** Value used for display and "is-selected" state: internal choice wins until parent updates selectedValue. */
  get effectiveValue(): any {
    const fallback = this.selectedValue;
    if (this.multiple) {
      const raw =
        this._displayValue !== undefined ? this._displayValue : fallback;
      return Array.isArray(raw) ? raw : [];
    }
    return this._displayValue !== undefined ? this._displayValue : fallback;
  }

  /** Screen reader: actual selection when trigger shows only placeholder. */
  get ariaLabelForTrigger(): string | null {
    if (!this.alwaysShowPlaceholder || this.multiple) {
      return null;
    }
    const opt = this.options?.find((o) => o.value === this.effectiveValue);
    if (!opt) {
      return this.placeholder ? String(this.placeholder) : null;
    }
    return this.labelsAlreadyTranslated ? opt.label : this.translate.instant(opt.label);
  }

  get displayLabel(): string {
    if (this.alwaysShowPlaceholder) {
      return this.placeholder != null && this.placeholder !== '' ? String(this.placeholder) : '';
    }
    if (this.multiple) {
      const vals = this.effectiveValue as unknown[];
      if (!vals.length) {
        return this.placeholder ? this.translate.instant(this.placeholder) : '';
      }
      const n = vals.length;
      const maxInline = Math.max(0, this.multipleSummaryAfter);
      if (n > maxInline) {
        return this.translate.instant('CustomSelect.multipleItemsSelected', {
          count: n,
        });
      }
      const labels = vals.map((v) => {
        const option = this.options?.find((o) => o.value === v);
        if (option) {
          return this.labelsAlreadyTranslated
            ? option.label
            : this.translate.instant(option.label);
        }
        return String(v);
      });
      return labels.join(', ');
    }
    if (this.effectiveValue === 'all' && this.showAllOption) {
      return this.translate.instant('All');
    }
    const option = this.options?.find((o) => o.value === this.effectiveValue);
    if (option) {
      return this.labelsAlreadyTranslated
        ? option.label
        : this.translate.instant(option.label);
    }
    return this.placeholder ? this.translate.instant(this.placeholder) : '';
  }

  /** Optional Feather icon name on the selected option (e.g. ticket status). */
  get selectedOptionFeatherIcon(): string | null {
    if (this.multiple || this.alwaysShowPlaceholder) {
      return null;
    }
    const option = this.options?.find((o) => o.value === this.effectiveValue);
    return option?.featherIcon ?? null;
  }

  /** CSS tone for {@link selectedOptionFeatherIcon} (`success` | `warning` | …). */
  get selectedOptionIconToneClass(): string | null {
    if (this.multiple || this.alwaysShowPlaceholder) {
      return null;
    }
    const option = this.options?.find((o) => o.value === this.effectiveValue);
    const tone = option?.iconTone;
    return tone ? `select-trigger__option-icon--${tone}` : null;
  }

  /** Muted trigger text when showing placeholder (no matching option). */
  get showPlaceholderTriggerStyle(): boolean {
    if (this.alwaysShowPlaceholder) {
      return true;
    }
    if (this.multiple) {
      return false;
    }
    return !this.options?.some((o) => o.value === this.effectiveValue);
  }

  trackByOptionValue(index: number, option: { value?: unknown } | null): unknown {
    return option?.value ?? index;
  }

  isOptionSelected(optionValue: any): boolean {
    if (this.multiple) {
      const list = this.effectiveValue as unknown[];
      return Array.isArray(list) && list.includes(optionValue);
    }
    return this.effectiveValue === optionValue;
  }

  /** Full comma-separated labels for `title` when many items are selected (summary in trigger). */
  get multipleSelectionTitle(): string {
    if (!this.multiple) {
      return '';
    }
    const vals = this.effectiveValue as unknown[];
    if (!vals.length) {
      return '';
    }
    const maxInline = Math.max(0, this.multipleSummaryAfter);
    if (vals.length <= maxInline) {
      return '';
    }
    return vals
      .map((v) => {
        const option = this.options?.find((o) => o.value === v);
        if (option) {
          return this.labelsAlreadyTranslated
            ? option.label
            : this.translate.instant(option.label);
        }
        return String(v);
      })
      .join(', ');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedValue']) {
      const v = changes['selectedValue'].currentValue;
      if (this.multiple) {
        this._displayValue = Array.isArray(v) ? [...v] : [];
      } else {
        this._displayValue = v;
      }
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.removeCloseListener();
    this.removePositionListeners();
  }

  onTriggerClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.toggleOpen();
  }

  toggleOpen(): void {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.hoveredValue = null;
      this.suppressOutsideCloseUntil = Date.now() + 300;
      this.updatePanelPosition();
      this.addPositionListeners();
      setTimeout(() => this.addCloseListener(), 0);
    } else {
      this.panelPosition = null;
      this.removeCloseListener();
      this.removePositionListeners();
    }
    this.cdr.markForCheck();
  }

  private updatePanelPosition(): void {
    if (this.panelLayout !== 'overlay' || !this.triggerBtn?.nativeElement) {
      this.panelPosition = null;
      return;
    }
    const rect = this.triggerBtn.nativeElement.getBoundingClientRect();
    this.panelPosition = {
      top: `${Math.round(rect.bottom)}px`,
      left: `${Math.round(rect.left)}px`,
      minWidth: `${Math.round(rect.width)}px`,
    };
  }

  private onViewportChange = (): void => {
    if (!this.isOpen) {
      return;
    }
    this.updatePanelPosition();
    this.cdr.markForCheck();
  };

  private addPositionListeners(): void {
    window.addEventListener('resize', this.onViewportChange, true);
    window.addEventListener('scroll', this.onViewportChange, true);
  }

  private removePositionListeners(): void {
    window.removeEventListener('resize', this.onViewportChange, true);
    window.removeEventListener('scroll', this.onViewportChange, true);
  }

  private closeListener = (event: MouseEvent): void => {
    if (Date.now() < this.suppressOutsideCloseUntil) {
      return;
    }
    const target = event.target as Node | null;
    if (this.wrapper?.nativeElement?.contains(target)) {
      return;
    }
    // Overlay panel is position:fixed; still part of wrapper DOM, but guard explicitly.
    const el = target as HTMLElement | null;
    if (el?.closest?.('.select-panel')) {
      return;
    }
    this.isOpen = false;
    this.panelPosition = null;
    this.removeCloseListener();
    this.removePositionListeners();
    this.cdr.markForCheck();
  };

  private addCloseListener(): void {
    document.addEventListener('mousedown', this.closeListener, true);
  }

  private removeCloseListener(): void {
    document.removeEventListener('mousedown', this.closeListener, true);
  }

  selectOption(value: any): void {
    if (this.multiple) {
      const current = Array.isArray(this.effectiveValue)
        ? [...(this.effectiveValue as unknown[])]
        : [];
      const idx = current.indexOf(value);
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        current.push(value);
      }
      this._displayValue = current;
      this.selectedValueChange.emit([...current]);
      this.hoveredValue = null;
      this.cdr.markForCheck();
      return;
    }
    this._displayValue = value;
    this.selectedValueChange.emit(value);
    this.isOpen = false;
    this.panelPosition = null;
    this.removeCloseListener();
    this.removePositionListeners();
    this.hoveredValue = null;
    this.cdr.markForCheck();
  }
}
